/**
 * API Client med automatisk token refresh och rate limit handling
 * 
 * Hanterar alla autentiserade API-anrop och försöker
 * automatiskt förnya access token om den har gått ut.
 * Hanterar även rate limiting (429) med exponential backoff.
 */

import { useAuthStore, User } from '@/stores/auth-store';
import { useToastStore } from '@/stores/toast-store';
import { AuthRefreshError, authService, getStoredRefreshCsrfToken } from './auth-service';

const devLog = {
  debug: (...args: unknown[]) => {
    if (import.meta.env.DEV) console.debug(...args);
  },
  info: (...args: unknown[]) => {
    if (import.meta.env.DEV) console.info(...args);
  },
  error: (...args: unknown[]) => {
    if (import.meta.env.DEV) console.error(...args);
  },
};

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:7071/api';
const IS_RUNTIME_PROD = (import.meta.env.VITE_ENVIRONMENT || '').toLowerCase() === 'prod';
// Flag för att undvika flera samtidiga refresh-försök
let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;

// Rate limiting: Track last rate limit error per endpoint
const rateLimitBackoff = new Map<string, { retryAfter: Date; attempts: number }>();

/**
 * Hantera rate limit med exponential backoff
 */
function handleRateLimit(endpoint: string, retryAfter?: number): { shouldRetry: boolean; waitMs: number } {
  const backoffInfo = rateLimitBackoff.get(endpoint);
  const now = new Date();

  // Om vi har en retry-after header, använd den
  if (retryAfter && retryAfter > 0) {
    const waitMs = retryAfter * 1000; // Convert seconds to ms
    rateLimitBackoff.set(endpoint, {
      retryAfter: new Date(now.getTime() + waitMs),
      attempts: (backoffInfo?.attempts || 0) + 1,
    });

    // Max 3 retry-försök
    if ((backoffInfo?.attempts || 0) >= 3) {
      return { shouldRetry: false, waitMs: 0 };
    }

    return { shouldRetry: true, waitMs };
  }

  // Exponential backoff utan retry-after header
  const attempts = (backoffInfo?.attempts || 0) + 1;
  const waitMs = Math.min(1000 * Math.pow(2, attempts - 1), 30000); // Max 30s

  rateLimitBackoff.set(endpoint, {
    retryAfter: new Date(now.getTime() + waitMs),
    attempts,
  });

  if (attempts >= 3) {
    return { shouldRetry: false, waitMs: 0 };
  }

  return { shouldRetry: true, waitMs };
}

/**
 * Rensa rate limit backoff för en endpoint
 */
function clearRateLimitBackoff(endpoint: string) {
  rateLimitBackoff.delete(endpoint);
}

function getApiErrorMessage(errJson: unknown): string | null {
  if (!errJson || typeof errJson !== 'object') return null;

  const payload = errJson as { error?: unknown; message?: unknown };
  if (typeof payload.message === 'string' && payload.message.trim()) {
    return payload.message;
  }

  if (typeof payload.error === 'string' && payload.error.trim()) {
    return payload.error;
  }

  if (payload.error && typeof payload.error === 'object') {
    const nested = payload.error as { message?: unknown };
    if (typeof nested.message === 'string' && nested.message.trim()) {
      return nested.message;
    }
  }

  return null;
}

function isPremiumAccessError(message: string | null): boolean {
  const normalizedMessage = message?.trim().toLowerCase() || '';

  return normalizedMessage.includes('premium subscription required')
    || normalizedMessage.includes('premium krävs')
    || normalizedMessage.includes('premium-only');
}

async function handleDeletedUserResponse(response: Response, endpoint: string, message: string | null): Promise<boolean> {
  const normalizedMessage = message?.trim().toLowerCase() || '';
  const isDeletedUser = response.status === 404 && (
    endpoint === '/users/me' ||
    normalizedMessage === 'user not found' ||
    normalizedMessage.includes('user not found')
  );

  if (!isDeletedUser) {
    return false;
  }

  devLog.info('[apiRequest] Deleted user detected, logging out immediately', { endpoint, status: response.status });
  await useAuthStore.getState().logout();
  return true;
}

/**
 * Vänta en viss tid (används för rate limit retry)
 */
function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Validate group-scoped endpoints so we never send requests with mock/invalid group ids.
 */
function getInvalidGroupEndpointReason(endpoint: string): string | null {
  const path = endpoint.split('?')[0] || '';
  if (!path.startsWith('/groups/')) return null;

  const rest = path.slice('/groups/'.length);
  if (!rest) return null; // '/groups' list endpoint

  const firstSegmentRaw = rest.split('/')[0] ?? '';
  const firstSegment = decodeURIComponent(firstSegmentRaw).trim();

  // Known non-groupId route under /groups
  if (firstSegment === 'unread-counts') return null;

  if (!firstSegment || firstSegment === 'undefined' || firstSegment === 'null') {
    return 'Invalid group endpoint: missing groupId';
  }

  const normalized = firstSegment.toLowerCase();
  if (normalized === 'mock' || normalized === 'mockgroup' || normalized === 'demo' || firstSegment === '1') {
    return `Blocked invalid groupId "${firstSegment}"`;
  }

  return null;
}

/**
 * Försök förnya access token med refresh token
 */
async function tryRefreshToken(): Promise<string | null> {
  // Om vi redan håller på att förnya, vänta på det
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  const refreshCsrfCookieName = import.meta.env.VITE_REFRESH_CSRF_COOKIE_NAME || 'bh_refresh_csrf';
  let legacyRefreshToken: string | null = null;
  let storageReadable = true;
  try {
    legacyRefreshToken = typeof window !== 'undefined' ? localStorage.getItem('refreshToken') : null;
  } catch {
    storageReadable = false;
  }
  const hasLegacyRefreshToken = !!legacyRefreshToken;
  const hasStoredRefreshCsrfToken = !!getStoredRefreshCsrfToken();
  const hasRefreshCsrfCookie = typeof document !== 'undefined'
    && document.cookie.split('; ').some((cookie) => cookie.startsWith(`${encodeURIComponent(refreshCsrfCookieName)}=`));

  if (!hasLegacyRefreshToken && !hasStoredRefreshCsrfToken && !hasRefreshCsrfCookie) {
    if (storageReadable && useAuthStore.getState().isAuthenticated) {
      useAuthStore.getState().logoutLocal();
    }
    return null;
  }

  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      const response = await authService.refreshTokenWithRetry(legacyRefreshToken || undefined);

      // Uppdatera auth store med nya tokens. The refresh endpoint returns
      // token payload but may not include the full user object. Update the
      // access token always and update the user only if provided.
      if (response.accessToken) {
        const expiresIn = response.expiresIn || undefined;
        useAuthStore.getState().setAccessToken(response.accessToken, expiresIn);
      }
      if (response && (response as unknown as { user?: unknown }).user) {
        const u = (response as unknown as { user?: unknown }).user as User;
        useAuthStore.getState().setUser(u);
        useAuthStore.getState().setHasHydrated(true);
      }

      // Spara nya refresh token om server gav en
      if (response.refreshToken) {
        try {
          if (IS_RUNTIME_PROD) localStorage.removeItem('refreshToken');
          else localStorage.setItem('refreshToken', response.refreshToken);
        } catch { /* ignore */ }
      }

      return response.accessToken;
    } catch (error) {
      devLog.error('Token refresh failed:', error);

      if (error instanceof AuthRefreshError && (error.kind === 'blocked' || error.kind === 'session-ended' || error.kind === 'user-deleted')) {
        if (error.code === 'IP_BLOCKED') {
          useToastStore.getState().push({
            title: 'IP-adress blockerad',
            description: error.message.replace('IP_BLOCKED: ', ''),
            variant: 'error',
          });
        } else if (error.code === 'USER_BLOCKED') {
          useToastStore.getState().push({
            title: 'Konto blockerat',
            description: error.message.replace('USER_BLOCKED: ', ''),
            variant: 'error',
          });
        }

        useAuthStore.getState().logoutLocal();
        if (error.kind === 'user-deleted') {
          window.location.replace('/');
        }
      }

      return null;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

/**
 * Gör ett autentiserat API-anrop med automatisk token refresh och rate limit retry
 */
export async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {},
  retryOnRateLimit: boolean = true
): Promise<{ success: boolean; data?: T; error?: string }> {
  const invalidGroupReason = getInvalidGroupEndpointReason(endpoint);
  if (invalidGroupReason) {
    devLog.error('[apiRequest] blocked request', { endpoint, reason: invalidGroupReason });
    return { success: false, error: invalidGroupReason };
  }

  let { accessToken } = useAuthStore.getState();
  const { isAuthenticated } = useAuthStore.getState();
  
  // Om inte autentiserad, försök cookie-/refresh-baserad tokenförnyelse först
  if (!isAuthenticated || !accessToken) {
    accessToken = await tryRefreshToken();
    if (!accessToken) {
      return { success: false, error: 'Not authenticated' };
    }
  }

  const url = `${API_BASE_URL}${endpoint}`;
  
  // Debug: log request details to help diagnose 404s
  try {
    devLog.debug('[apiRequest] url=', url, 'method=', options.method || 'GET', 'body=', options.body);
  } catch { /* ignore logging errors */ }

  // Gör första försöket
  let response = await fetch(url, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
      ...options.headers,
    },
  });

  // Om 429 Too Many Requests, hantera rate limiting
  if (response.status === 429 && retryOnRateLimit) {
    try {
      const retryAfterHeader = response.headers.get('Retry-After');
      const retryAfter = retryAfterHeader ? parseInt(retryAfterHeader, 10) : undefined;
      
      const backoff = handleRateLimit(endpoint, retryAfter);
      
      if (backoff.shouldRetry) {
        // Visa toast om vi väntar länge
        if (backoff.waitMs > 2000) {
          useToastStore.getState().push({
            title: 'Rate limit nådd',
            description: `För många förfrågningar. Försöker igen om ${Math.ceil(backoff.waitMs / 1000)} sekunder...`,
            variant: 'info',
          });
        }

        // Vänta och försök igen
        await sleep(backoff.waitMs);
        
        // Retry request (men utan ytterligare retries för att undvika infinite loop)
        return apiRequest<T>(endpoint, options, false);
      } else {
        // Max retries nådd
        const errJson = await response.json().catch(() => ({}));
        const message = errJson?.error?.message || 'För många förfrågningar. Försök igen senare.';
        
        useToastStore.getState().push({
          title: 'Rate limit nådd',
          description: message,
          variant: 'error',
        });
        
        return { success: false, error: message };
      }
    } catch (err) {
      devLog.error('Rate limit handling failed:', err);
      // Fortsätt med vanlig felhantering
    }
  }

  // Rensa rate limit backoff vid lyckad request
  if (response.ok) {
    clearRateLimitBackoff(endpoint);
  }

  // Check for stale token (subscription upgraded by admin)
  if (response.status === 401 && response.headers.get('X-Token-Stale') === 'true') {
    devLog.info('[apiRequest] Token stale detected - subscription upgraded, refreshing token');
    const newToken = await tryRefreshToken();
    
    if (newToken) {
      // Retry with fresh token
      response = await fetch(url, {
        ...options,
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${newToken}`,
          ...options.headers,
        },
      });
      
      // If still not ok after token refresh, fall through to normal error handling
      if (response.ok) {
        clearRateLimitBackoff(endpoint);
      }
    } else {
      return { success: false, error: 'Failed to refresh token after subscription upgrade' };
    }
  }
  // Om 401 Unauthorized (and not stale token), försök förnya token och gör ett nytt anrop
  else if (response.status === 401) {
    const newToken = await tryRefreshToken();
    
    if (newToken) {
      // Försök igen med ny token
      response = await fetch(url, {
        ...options,
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${newToken}`,
          ...options.headers,
        },
      });
    } else {
      // Debug: log non-401 non-ok statuses for visibility
      if (!response.ok) {
        try { devLog.debug('[apiRequest] non-ok response', { url, status: response.status }); } catch { /* ignore */ }
      }

      // Kunde inte förnya token
      return { success: false, error: 'Session expired' };
    }
  }

  // Om 403, kontrollera om det är IP-blockerad eller användare blockerad
  if (response.status === 403) {
    try {
      const errJson = await response.clone().json();
      // Check both error formats: old format (error === 'IP_BLOCKED') and new format (error.code === 'IP_BLOCKED')
      const isIPBlocked = errJson.error === 'IP_BLOCKED' || (errJson.error && errJson.error.code === 'IP_BLOCKED');
      const isUserBlocked = errJson.error === 'USER_BLOCKED' || (errJson.error && errJson.error.code === 'USER_BLOCKED');
      
      if (isIPBlocked || isUserBlocked) {
        const message = typeof errJson.error === 'object' ? errJson.error.message : errJson.message;
        const title = isIPBlocked ? 'IP-adress blockerad' : 'Konto blockerat';
        
        // Visa toast-meddelande
        useToastStore.getState().push({
          title,
          description: message || (isIPBlocked 
            ? 'Din IP-adress har blockerats på grund av misstänkt aktivitet. Du har loggats ut.'
            : 'Ditt konto har blockerats. Kontakta support för mer information.'),
          variant: 'error',
        });
        
        // Logga ut användaren (rensar localStorage, sessionStorage och cache)
        useAuthStore.getState().logout();
        
        // Vänta lite så användaren hinner se meddelandet innan omdirigering
        setTimeout(() => {
          window.location.href = '/';
        }, 1500);
        
        return { success: false, error: message || (isIPBlocked ? 'IP-adress blockerad' : 'Konto blockerat') };
      }

      const message = getApiErrorMessage(errJson);
      if (isPremiumAccessError(message)) {
        devLog.info('[apiRequest] Premium-related 403 detected, refreshing token before retry', { endpoint });
        const newToken = await tryRefreshToken();

        if (newToken) {
          response = await fetch(url, {
            ...options,
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${newToken}`,
              ...options.headers,
            },
          });

          if (response.ok) {
            clearRateLimitBackoff(endpoint);
          }
        }
      }
    } catch {
      // Om vi inte kan parsa JSON, fortsätt med vanlig felhantering
    }
  }

  // Om fortfarande inte ok, försök parsa felkropp och returnera detaljer
  if (!response.ok) {
    try {
      const errJson = await response.json();
      const message = getApiErrorMessage(errJson);

      if (await handleDeletedUserResponse(response, endpoint, message)) {
        return { success: false, error: message || 'User not found', data: undefined };
      }

      // Stöd för vårt API:s felformat { success: false, error: { code, message, details } }
      if (errJson && errJson.error) {
        const apiMessage = (errJson.error && (errJson.error.message || JSON.stringify(errJson.error))) || 'Unknown error';
        return { success: false, error: `${apiMessage} (${response.status} ${url})`, data: undefined };
      }
      return { success: false, error: `Request failed: ${response.status} ${url}`, data: undefined };
    } catch {
      if (await handleDeletedUserResponse(response, endpoint, null)) {
        return { success: false, error: 'User not found', data: undefined };
      }
      return { success: false, error: `Request failed: ${response.status} ${url}` };
    }
  }

  // Om ingen content (204 No Content)
  if (response.status === 204) {
    return { success: true };
  }

  try {
    const json = await response.json();
    return { success: true, data: json.data || json };
  } catch {
    return { success: true };
  }
}

/**
 * GET request
 */
export function apiGet<T>(endpoint: string) {
  return apiRequest<T>(endpoint, { method: 'GET' });
}

/**
 * Anonymous GET request that doesn't require auth headers (for public endpoints)
 */
export async function apiAnonGet(endpoint: string) {
  const url = `${API_BASE_URL}${endpoint}`;
  try {
    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      try {
        const errJson = await response.json();
        if (errJson && errJson.error) {
          const message = (errJson.error && (errJson.error.message || JSON.stringify(errJson.error))) || 'Unknown error';
          return { success: false, error: `${message} (${response.status} ${url})` };
        }
        return { success: false, error: `Request failed: ${response.status} ${url}` };
      } catch {
        return { success: false, error: `Request failed: ${response.status} ${url}` };
      }
    }

    if (response.status === 204) return { success: true };

    try {
      const json = await response.json();
      return { success: true, data: json.data || json };
    } catch {
      return { success: true };
    }
  } catch (err) {
    return { success: false, error: String(err) };
  }
}

/**
 * POST request
 */
export function apiPost<T>(endpoint: string, data?: unknown) {
  return apiRequest<T>(endpoint, {
    method: 'POST',
    body: data ? JSON.stringify(data) : undefined,
  });
}

/**
 * PUT request
 */
export function apiPut<T>(endpoint: string, data?: unknown) {
  return apiRequest<T>(endpoint, {
    method: 'PUT',
    body: data ? JSON.stringify(data) : undefined,
  });
}

/**
 * DELETE request
 */
export function apiDelete<T>(endpoint: string) {
  return apiRequest<T>(endpoint, { method: 'DELETE' });
}

/**
 * Register push notification token
 */
export async function registerPushToken(pushToken: string): Promise<{ success: boolean; error?: string }> {
  return apiPost('/notifications/register-token', { pushToken });
}

/**
 * Unregister push notification token
 */
export async function unregisterPushToken(): Promise<{ success: boolean; error?: string }> {
  return apiDelete('/notifications/register-token');
}

/**
 * Mark a thread as read
 */
export async function markThreadAsRead(groupId: string, threadId: string): Promise<{ success: boolean; error?: string }> {
  return apiPost(`/groups/${groupId}/threads/${threadId}/read`);
}

/**
 * Get all thread read statuses for a group
 * Returns a map: { [threadId]: lastReadAt }
 */
export async function getThreadReadStatuses(groupId: string): Promise<{ success: boolean; data?: Record<string, string>; error?: string }> {
  return apiGet<Record<string, string>>(`/groups/${groupId}/threads-read-status`);
}

/**
 * Get unread thread counts for all user's groups
 * Returns a map: { [groupId]: { unreadThreads: number, newBoardNotes: number, newCalendarEvents: number, newWikiArticles: number } }
 */
export async function getUnreadCounts(): Promise<{ 
  success: boolean; 
  data?: Record<string, { unreadThreads: number; newBoardNotes: number; newCalendarEvents: number; newWikiArticles: number }>; 
  error?: string 
}> {
  return apiGet<Record<string, { unreadThreads: number; newBoardNotes: number; newCalendarEvents: number; newWikiArticles: number }>>('/groups/unread-counts');
}

/**
 * Update last visited timestamp for a group tab
 */
export async function updateTabVisit(groupId: string, tabName: 'board' | 'calendar' | 'wiki'): Promise<{ success: boolean; error?: string }> {
  return apiPost(`/groups/${groupId}/tab-visit`, { tabName });
}

/**
 * Get last visited timestamp for a group tab
 */
export async function getTabVisit(groupId: string, tabName: 'board' | 'calendar' | 'wiki'): Promise<{ 
  success: boolean; 
  data?: { lastVisitedAt: string | null }; 
  error?: string 
}> {
  return apiGet<{ lastVisitedAt: string | null }>(`/groups/${groupId}/tabs/${tabName}/last-visit`);
}
